import { Currency, Incoterms, TradeSignalSourceType, useApiAccountsV1UserRetrieve } from 'api';
import { dayjs, formatDateToIsoString } from 'date';
import { Form, generatePriceFieldValues, useForm, yupResolver } from 'form';
import { useTranslation } from 'i18n';
import { useNavigate } from 'react-router-dom';
import { ActionRow, Button, Page, Section } from 'ui';

import { SupplySignalFormSection } from '../../components/SupplySignalFormSection/SupplySignalFormSection';
import { useCreateSupplySignal } from '../../hooks/useCreateSupplySignal/useCreateSupplySignal';
import {
  type SupplySignalFormInputValues,
  type SupplySignalFormValues,
  SupplySignalSchema,
} from '../../schema/SupplySignalSchema/SupplySignalSchema';

const DEFAULT_VALUES: Partial<SupplySignalFormValues> = {
  currency: Currency.GBP,
  incoterm: Incoterms.EXW,
  last_collection_date: import.meta.env.VITE_CI ? dayjs().add(2, 'day') : undefined,
  notes: '',
  price_as_index_pct_enabled: false,
  sale_target_date: import.meta.env.VITE_CI ? dayjs().add(3, 'day') : undefined,
  source_type: TradeSignalSourceType.GROUND_CONTROL,
};

export const SupplySignalCreate = () => {
  const { t } = useTranslation('orders');
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const { data: user } = useApiAccountsV1UserRetrieve();

  const form = useForm<SupplySignalFormInputValues, unknown, SupplySignalFormValues>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(SupplySignalSchema),
  });

  const { mutate: createSupplySignal, isPending } = useCreateSupplySignal(form);

  const onSubmit = (values: SupplySignalFormValues) => {
    const {
      trade_company_lead_id,
      trade_company_id,
      last_collection_date,
      sale_target_date,
      tonnes,
      tonnes_per_load,
      // eslint-disable-next-line unused-imports/no-unused-vars
      price_as_index_percentage,
      ...restValues
    } = values;
    createSupplySignal({
      data: {
        ...restValues,
        ...generatePriceFieldValues(values),
        created_by_id: user?.id,
        last_collection_date: formatDateToIsoString(last_collection_date),
        port_of_loading: {
          id: restValues.port_of_loading_id,
        },
        sale_target_date: sale_target_date ? formatDateToIsoString(sale_target_date) : undefined,
        seller: {
          trade_company_id: trade_company_id,
        },
        seller_lead_id: trade_company_lead_id,
        tonnes: tonnes.toString(),
        tonnes_per_load: tonnes_per_load.toString(),
      },
    });
  };

  return (
    <Page>
      <Page.Title title={t('tradeSignals.supplyPage.title')} />
      <Page.Divider />
      <Section>
        <Form<SupplySignalFormInputValues, unknown, SupplySignalFormValues> {...form} onSubmit={onSubmit}>
          <SupplySignalFormSection />
          <ActionRow>
            <Button fullWidth onClick={goBack} variant="secondary">
              {t('tradeSignals.common.actions.cancel')}
            </Button>
            <Button fullWidth loading={isPending} type="submit">
              {t('tradeSignals.common.actions.save')}
            </Button>
          </ActionRow>
        </Form>
      </Section>
    </Page>
  );
};
